/**
 * Generated by orval v7.21.0 🍺
 * Do not edit manually.
 * Rocket.net API
 * Rocket.net - All APIs
 * OpenAPI spec version: 1.0.0
 */
import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
  UseSuspenseQueryOptions,
  UseSuspenseQueryResult,
} from '@tanstack/react-query';

import { customFetch } from '../../custom-fetch';
import { addMetaToMutation } from '../../mutators/add-meta';
import { addMetaToQuery } from '../../mutators/add-meta';
import type {
  AcceptSiteUserInviteWhileLoggedInParams,
  CreateSiteUserPasswordRequest,
  CreateSiteUserRequest,
  CreateSiteUserResponse,
  Error,
  InviteSiteUserRequest,
  InviteSiteUserResponse,
  ListSiteSiteUsersResponse,
  ListSiteUserSitesParams,
  ListSiteUserSitesResponse,
  ListSiteUsersParams,
  LoginRequest,
  LoginResponse,
  ResetSiteUserPasswordRequest,
  SiteID,
  SuccessResponse,
  UpdateSiteUserRequest,
} from '../rocketNetAPI.schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

/**
 * Creates a site-user profile with name and email before site invitations are sent.
 * @summary Register as a site collaborator
 */
export const createSiteUser = (
  createSiteUserRequest: CreateSiteUserRequest,
  signal?: AbortSignal
) => {
  return customFetch<CreateSiteUserResponse>({
    url: `/v1/sites/users`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: createSiteUserRequest,
    signal,
  });
};

export const getCreateSiteUserMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createSiteUser>>,
    TError,
    { data: CreateSiteUserRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createSiteUser>>,
  TError,
  { data: CreateSiteUserRequest },
  TContext
> => {
  const mutationKey = ['createSiteUser'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createSiteUser>>,
    { data: CreateSiteUserRequest }
  > = props => {
    const { data } = props ?? {};

    return createSiteUser(data);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/sites/users` },
    {
      operationId: 'app.controllers.site_users_controller.create_site_user',
      operationName: 'createSiteUser',
    }
  );

  return customOptions;
};

export type CreateSiteUserMutationResult = NonNullable<Awaited<ReturnType<typeof createSiteUser>>>;
export type CreateSiteUserMutationBody = CreateSiteUserRequest;
export type CreateSiteUserMutationError = Error;

/**
 * @summary Register as a site collaborator
 */
export const useCreateSiteUser = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createSiteUser>>,
      TError,
      { data: CreateSiteUserRequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof createSiteUser>>,
  TError,
  { data: CreateSiteUserRequest },
  TContext
> => {
  const mutationOptions = getCreateSiteUserMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Lists sites you can access as a site user, with domain, status, disk usage, and connection details.
 * @summary List my assigned sites
 */
export const listSiteUserSites = (params?: ListSiteUserSitesParams, signal?: AbortSignal) => {
  return customFetch<ListSiteUserSitesResponse>({
    url: `/v1/sites/users`,
    method: 'GET',
    params,
    signal,
  });
};

export const getListSiteUserSitesQueryKey = (params?: ListSiteUserSitesParams) => {
  return [`/v1/sites/users`, ...(params ? [params] : [])] as const;
};

export const getListSiteUserSitesQueryOptions = <
  TData = Awaited<ReturnType<typeof listSiteUserSites>>,
  TError = Error,
>(
  params?: ListSiteUserSitesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSiteUserSites>>, TError, TData>>;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListSiteUserSitesQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listSiteUserSites>>> = ({ signal }) =>
    listSiteUserSites(params, signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    { params },
    {
      url: `/v1/sites/users`,
      operationId: 'app.controllers.site_users_controller.list_site_user_sites',
      operationName: 'listSiteUserSites',
    }
  );

  return customOptions as UseQueryOptions<
    Awaited<ReturnType<typeof listSiteUserSites>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListSiteUserSitesQueryResult = NonNullable<
  Awaited<ReturnType<typeof listSiteUserSites>>
>;
export type ListSiteUserSitesQueryError = Error;

export function useListSiteUserSites<
  TData = Awaited<ReturnType<typeof listSiteUserSites>>,
  TError = Error,
>(
  params: undefined | ListSiteUserSitesParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSiteUserSites>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listSiteUserSites>>,
          TError,
          Awaited<ReturnType<typeof listSiteUserSites>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListSiteUserSites<
  TData = Awaited<ReturnType<typeof listSiteUserSites>>,
  TError = Error,
>(
  params?: ListSiteUserSitesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSiteUserSites>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listSiteUserSites>>,
          TError,
          Awaited<ReturnType<typeof listSiteUserSites>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListSiteUserSites<
  TData = Awaited<ReturnType<typeof listSiteUserSites>>,
  TError = Error,
>(
  params?: ListSiteUserSitesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSiteUserSites>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List my assigned sites
 */

export function useListSiteUserSites<
  TData = Awaited<ReturnType<typeof listSiteUserSites>>,
  TError = Error,
>(
  params?: ListSiteUserSitesParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSiteUserSites>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListSiteUserSitesQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

export const getListSiteUserSitesSuspenseQueryOptions = <
  TData = Awaited<ReturnType<typeof listSiteUserSites>>,
  TError = Error,
>(
  params?: ListSiteUserSitesParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listSiteUserSites>>, TError, TData>
    >;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListSiteUserSitesQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listSiteUserSites>>> = ({ signal }) =>
    listSiteUserSites(params, signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    { params },
    {
      url: `/v1/sites/users`,
      operationId: 'app.controllers.site_users_controller.list_site_user_sites',
      operationName: 'listSiteUserSites',
    }
  );

  return customOptions as UseSuspenseQueryOptions<
    Awaited<ReturnType<typeof listSiteUserSites>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListSiteUserSitesSuspenseQueryResult = NonNullable<
  Awaited<ReturnType<typeof listSiteUserSites>>
>;
export type ListSiteUserSitesSuspenseQueryError = Error;

export function useListSiteUserSitesSuspense<
  TData = Awaited<ReturnType<typeof listSiteUserSites>>,
  TError = Error,
>(
  params: undefined | ListSiteUserSitesParams,
  options: {
    query: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listSiteUserSites>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListSiteUserSitesSuspense<
  TData = Awaited<ReturnType<typeof listSiteUserSites>>,
  TError = Error,
>(
  params?: ListSiteUserSitesParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listSiteUserSites>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListSiteUserSitesSuspense<
  TData = Awaited<ReturnType<typeof listSiteUserSites>>,
  TError = Error,
>(
  params?: ListSiteUserSitesParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listSiteUserSites>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List my assigned sites
 */

export function useListSiteUserSitesSuspense<
  TData = Awaited<ReturnType<typeof listSiteUserSites>>,
  TError = Error,
>(
  params?: ListSiteUserSitesParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listSiteUserSites>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListSiteUserSitesSuspenseQueryOptions(params, options);

  const query = useSuspenseQuery(queryOptions, queryClient) as UseSuspenseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Signs in with site-user credentials to get an API token scoped to assigned sites.
 * @summary Sign in as a site user
 */
export const siteUserLogin = (loginRequest: LoginRequest, signal?: AbortSignal) => {
  return customFetch<LoginResponse>({
    url: `/v1/sites/users/login`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: loginRequest,
    signal,
  });
};

export const getSiteUserLoginMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof siteUserLogin>>,
    TError,
    { data: LoginRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof siteUserLogin>>,
  TError,
  { data: LoginRequest },
  TContext
> => {
  const mutationKey = ['siteUserLogin'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof siteUserLogin>>,
    { data: LoginRequest }
  > = props => {
    const { data } = props ?? {};

    return siteUserLogin(data);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/sites/users/login` },
    {
      operationId: 'app.controllers.site_users_controller.site_user_login',
      operationName: 'siteUserLogin',
    }
  );

  return customOptions;
};

export type SiteUserLoginMutationResult = NonNullable<Awaited<ReturnType<typeof siteUserLogin>>>;
export type SiteUserLoginMutationBody = LoginRequest;
export type SiteUserLoginMutationError = Error;

/**
 * @summary Sign in as a site user
 */
export const useSiteUserLogin = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof siteUserLogin>>,
      TError,
      { data: LoginRequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof siteUserLogin>>,
  TError,
  { data: LoginRequest },
  TContext
> => {
  const mutationOptions = getSiteUserLoginMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Emails a password-reset link to a site user by email address.
 * @summary Request a password reset
 */
export const resetSiteUserPassword = (
  resetSiteUserPasswordRequest: ResetSiteUserPasswordRequest,
  signal?: AbortSignal
) => {
  return customFetch<SuccessResponse>({
    url: `/v1/sites/users/reset-password`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: resetSiteUserPasswordRequest,
    signal,
  });
};

export const getResetSiteUserPasswordMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof resetSiteUserPassword>>,
    TError,
    { data: ResetSiteUserPasswordRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof resetSiteUserPassword>>,
  TError,
  { data: ResetSiteUserPasswordRequest },
  TContext
> => {
  const mutationKey = ['resetSiteUserPassword'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof resetSiteUserPassword>>,
    { data: ResetSiteUserPasswordRequest }
  > = props => {
    const { data } = props ?? {};

    return resetSiteUserPassword(data);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/sites/users/reset-password` },
    {
      operationId: 'app.controllers.site_users_controller.reset_site_user_password',
      operationName: 'resetSiteUserPassword',
    }
  );

  return customOptions;
};

export type ResetSiteUserPasswordMutationResult = NonNullable<
  Awaited<ReturnType<typeof resetSiteUserPassword>>
>;
export type ResetSiteUserPasswordMutationBody = ResetSiteUserPasswordRequest;
export type ResetSiteUserPasswordMutationError = Error;

/**
 * @summary Request a password reset
 */
export const useResetSiteUserPassword = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof resetSiteUserPassword>>,
      TError,
      { data: ResetSiteUserPasswordRequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof resetSiteUserPassword>>,
  TError,
  { data: ResetSiteUserPasswordRequest },
  TContext
> => {
  const mutationOptions = getResetSiteUserPasswordMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Changes a site user's name, email, or password.
 * @summary Update site user profile
 */
export const modifySiteUser = (userId: number, updateSiteUserRequest: UpdateSiteUserRequest) => {
  return customFetch<SuccessResponse>({
    url: `/v1/sites/users/${userId}`,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    data: updateSiteUserRequest,
  });
};

export const getModifySiteUserMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof modifySiteUser>>,
    TError,
    { userId: number; data: UpdateSiteUserRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof modifySiteUser>>,
  TError,
  { userId: number; data: UpdateSiteUserRequest },
  TContext
> => {
  const mutationKey = ['modifySiteUser'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof modifySiteUser>>,
    { userId: number; data: UpdateSiteUserRequest }
  > = props => {
    const { userId, data } = props ?? {};

    return modifySiteUser(userId, data);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/sites/users/{userId}` },
    {
      operationId: 'app.controllers.site_users_controller.modify_site_user',
      operationName: 'modifySiteUser',
    }
  );

  return customOptions;
};

export type ModifySiteUserMutationResult = NonNullable<Awaited<ReturnType<typeof modifySiteUser>>>;
export type ModifySiteUserMutationBody = UpdateSiteUserRequest;
export type ModifySiteUserMutationError = Error;

/**
 * @summary Update site user profile
 */
export const useModifySiteUser = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof modifySiteUser>>,
      TError,
      { userId: number; data: UpdateSiteUserRequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof modifySiteUser>>,
  TError,
  { userId: number; data: UpdateSiteUserRequest },
  TContext
> => {
  const mutationOptions = getModifySiteUserMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Sets or resets a site user's password using the token from an invite or reset email.
 * @summary Set site user password
 */
export const setSiteUserPassword = (
  userId: number,
  createSiteUserPasswordRequest: CreateSiteUserPasswordRequest,
  signal?: AbortSignal
) => {
  return customFetch<SuccessResponse>({
    url: `/v1/sites/users/${userId}/password`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: createSiteUserPasswordRequest,
    signal,
  });
};

export const getSetSiteUserPasswordMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof setSiteUserPassword>>,
    TError,
    { userId: number; data: CreateSiteUserPasswordRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof setSiteUserPassword>>,
  TError,
  { userId: number; data: CreateSiteUserPasswordRequest },
  TContext
> => {
  const mutationKey = ['setSiteUserPassword'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof setSiteUserPassword>>,
    { userId: number; data: CreateSiteUserPasswordRequest }
  > = props => {
    const { userId, data } = props ?? {};

    return setSiteUserPassword(userId, data);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/sites/users/{userId}/password` },
    {
      operationId: 'app.controllers.site_users_controller.set_site_user_password',
      operationName: 'setSiteUserPassword',
    }
  );

  return customOptions;
};

export type SetSiteUserPasswordMutationResult = NonNullable<
  Awaited<ReturnType<typeof setSiteUserPassword>>
>;
export type SetSiteUserPasswordMutationBody = CreateSiteUserPasswordRequest;
export type SetSiteUserPasswordMutationError = Error;

/**
 * @summary Set site user password
 */
export const useSetSiteUserPassword = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof setSiteUserPassword>>,
      TError,
      { userId: number; data: CreateSiteUserPasswordRequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof setSiteUserPassword>>,
  TError,
  { userId: number; data: CreateSiteUserPasswordRequest },
  TContext
> => {
  const mutationOptions = getSetSiteUserPasswordMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Lists everyone invited to manage a specific site, with status and email.
 * @summary List site collaborators
 */
export const listSiteUsers = (id: SiteID, params?: ListSiteUsersParams, signal?: AbortSignal) => {
  return customFetch<ListSiteSiteUsersResponse>({
    url: `/v1/sites/${id}/users`,
    method: 'GET',
    params,
    signal,
  });
};

export const getListSiteUsersQueryKey = (id?: SiteID, params?: ListSiteUsersParams) => {
  return [`/v1/sites/${id}/users`, ...(params ? [params] : [])] as const;
};

export const getListSiteUsersQueryOptions = <
  TData = Awaited<ReturnType<typeof listSiteUsers>>,
  TError = Error,
>(
  id: SiteID,
  params?: ListSiteUsersParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSiteUsers>>, TError, TData>>;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListSiteUsersQueryKey(id, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listSiteUsers>>> = ({ signal }) =>
    listSiteUsers(id, params, signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    { id, params },
    {
      url: `/v1/sites/${id}/users`,
      operationId: 'app.controllers.site_users_controller.list_site_users',
      operationName: 'listSiteUsers',
    }
  );

  return customOptions as UseQueryOptions<
    Awaited<ReturnType<typeof listSiteUsers>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListSiteUsersQueryResult = NonNullable<Awaited<ReturnType<typeof listSiteUsers>>>;
export type ListSiteUsersQueryError = Error;

export function useListSiteUsers<TData = Awaited<ReturnType<typeof listSiteUsers>>, TError = Error>(
  id: SiteID,
  params: undefined | ListSiteUsersParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSiteUsers>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listSiteUsers>>,
          TError,
          Awaited<ReturnType<typeof listSiteUsers>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListSiteUsers<TData = Awaited<ReturnType<typeof listSiteUsers>>, TError = Error>(
  id: SiteID,
  params?: ListSiteUsersParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSiteUsers>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listSiteUsers>>,
          TError,
          Awaited<ReturnType<typeof listSiteUsers>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListSiteUsers<TData = Awaited<ReturnType<typeof listSiteUsers>>, TError = Error>(
  id: SiteID,
  params?: ListSiteUsersParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSiteUsers>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List site collaborators
 */

export function useListSiteUsers<TData = Awaited<ReturnType<typeof listSiteUsers>>, TError = Error>(
  id: SiteID,
  params?: ListSiteUsersParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSiteUsers>>, TError, TData>>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListSiteUsersQueryOptions(id, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

export const getListSiteUsersSuspenseQueryOptions = <
  TData = Awaited<ReturnType<typeof listSiteUsers>>,
  TError = Error,
>(
  id: SiteID,
  params?: ListSiteUsersParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listSiteUsers>>, TError, TData>
    >;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListSiteUsersQueryKey(id, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listSiteUsers>>> = ({ signal }) =>
    listSiteUsers(id, params, signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    { id, params },
    {
      url: `/v1/sites/${id}/users`,
      operationId: 'app.controllers.site_users_controller.list_site_users',
      operationName: 'listSiteUsers',
    }
  );

  return customOptions as UseSuspenseQueryOptions<
    Awaited<ReturnType<typeof listSiteUsers>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListSiteUsersSuspenseQueryResult = NonNullable<
  Awaited<ReturnType<typeof listSiteUsers>>
>;
export type ListSiteUsersSuspenseQueryError = Error;

export function useListSiteUsersSuspense<
  TData = Awaited<ReturnType<typeof listSiteUsers>>,
  TError = Error,
>(
  id: SiteID,
  params: undefined | ListSiteUsersParams,
  options: {
    query: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listSiteUsers>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListSiteUsersSuspense<
  TData = Awaited<ReturnType<typeof listSiteUsers>>,
  TError = Error,
>(
  id: SiteID,
  params?: ListSiteUsersParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listSiteUsers>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListSiteUsersSuspense<
  TData = Awaited<ReturnType<typeof listSiteUsers>>,
  TError = Error,
>(
  id: SiteID,
  params?: ListSiteUsersParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listSiteUsers>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List site collaborators
 */

export function useListSiteUsersSuspense<
  TData = Awaited<ReturnType<typeof listSiteUsers>>,
  TError = Error,
>(
  id: SiteID,
  params?: ListSiteUsersParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<Awaited<ReturnType<typeof listSiteUsers>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListSiteUsersSuspenseQueryOptions(id, params, options);

  const query = useSuspenseQuery(queryOptions, queryClient) as UseSuspenseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Sends a site access invitation by name and email. Invites expire after 7 days.
 * @summary Invite someone to a site
 */
export const inviteSiteUser = (
  id: SiteID,
  inviteSiteUserRequest: InviteSiteUserRequest,
  signal?: AbortSignal
) => {
  return customFetch<InviteSiteUserResponse>({
    url: `/v1/sites/${id}/users`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: inviteSiteUserRequest,
    signal,
  });
};

export const getInviteSiteUserMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof inviteSiteUser>>,
    TError,
    { id: SiteID; data: InviteSiteUserRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof inviteSiteUser>>,
  TError,
  { id: SiteID; data: InviteSiteUserRequest },
  TContext
> => {
  const mutationKey = ['inviteSiteUser'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof inviteSiteUser>>,
    { id: SiteID; data: InviteSiteUserRequest }
  > = props => {
    const { id, data } = props ?? {};

    return inviteSiteUser(id, data);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/sites/{id}/users` },
    {
      operationId: 'app.controllers.site_users_controller.invite_site_user',
      operationName: 'inviteSiteUser',
    }
  );

  return customOptions;
};

export type InviteSiteUserMutationResult = NonNullable<Awaited<ReturnType<typeof inviteSiteUser>>>;
export type InviteSiteUserMutationBody = InviteSiteUserRequest;
export type InviteSiteUserMutationError = Error;

/**
 * @summary Invite someone to a site
 */
export const useInviteSiteUser = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof inviteSiteUser>>,
      TError,
      { id: SiteID; data: InviteSiteUserRequest },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof inviteSiteUser>>,
  TError,
  { id: SiteID; data: InviteSiteUserRequest },
  TContext
> => {
  const mutationOptions = getInviteSiteUserMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Accepts a site invitation using the token from the invite email.
 * @summary Accept site invite (token)
 */
export const acceptSiteUserInviteWhileLoggedIn = (
  id: SiteID,
  params: AcceptSiteUserInviteWhileLoggedInParams,
  signal?: AbortSignal
) => {
  return customFetch<SuccessResponse>({
    url: `/v1/sites/${id}/users/accept`,
    method: 'GET',
    params,
    signal,
  });
};

export const getAcceptSiteUserInviteWhileLoggedInQueryKey = (
  id?: SiteID,
  params?: AcceptSiteUserInviteWhileLoggedInParams
) => {
  return [`/v1/sites/${id}/users/accept`, ...(params ? [params] : [])] as const;
};

export const getAcceptSiteUserInviteWhileLoggedInQueryOptions = <
  TData = Awaited<ReturnType<typeof acceptSiteUserInviteWhileLoggedIn>>,
  TError = Error,
>(
  id: SiteID,
  params: AcceptSiteUserInviteWhileLoggedInParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof acceptSiteUserInviteWhileLoggedIn>>, TError, TData>
    >;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getAcceptSiteUserInviteWhileLoggedInQueryKey(id, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof acceptSiteUserInviteWhileLoggedIn>>> = ({
    signal,
  }) => acceptSiteUserInviteWhileLoggedIn(id, params, signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    { id, params },
    {
      url: `/v1/sites/${id}/users/accept`,
      operationId: 'app.controllers.site_users_controller.accept_site_user_invite_while_logged_in',
      operationName: 'acceptSiteUserInviteWhileLoggedIn',
    }
  );

  return customOptions as UseQueryOptions<
    Awaited<ReturnType<typeof acceptSiteUserInviteWhileLoggedIn>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type AcceptSiteUserInviteWhileLoggedInQueryResult = NonNullable<
  Awaited<ReturnType<typeof acceptSiteUserInviteWhileLoggedIn>>
>;
export type AcceptSiteUserInviteWhileLoggedInQueryError = Error;

export function useAcceptSiteUserInviteWhileLoggedIn<
  TData = Awaited<ReturnType<typeof acceptSiteUserInviteWhileLoggedIn>>,
  TError = Error,
>(
  id: SiteID,
  params: AcceptSiteUserInviteWhileLoggedInParams,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof acceptSiteUserInviteWhileLoggedIn>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof acceptSiteUserInviteWhileLoggedIn>>,
          TError,
          Awaited<ReturnType<typeof acceptSiteUserInviteWhileLoggedIn>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useAcceptSiteUserInviteWhileLoggedIn<
  TData = Awaited<ReturnType<typeof acceptSiteUserInviteWhileLoggedIn>>,
  TError = Error,
>(
  id: SiteID,
  params: AcceptSiteUserInviteWhileLoggedInParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof acceptSiteUserInviteWhileLoggedIn>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof acceptSiteUserInviteWhileLoggedIn>>,
          TError,
          Awaited<ReturnType<typeof acceptSiteUserInviteWhileLoggedIn>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useAcceptSiteUserInviteWhileLoggedIn<
  TData = Awaited<ReturnType<typeof acceptSiteUserInviteWhileLoggedIn>>,
  TError = Error,
>(
  id: SiteID,
  params: AcceptSiteUserInviteWhileLoggedInParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof acceptSiteUserInviteWhileLoggedIn>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Accept site invite (token)
 */

export function useAcceptSiteUserInviteWhileLoggedIn<
  TData = Awaited<ReturnType<typeof acceptSiteUserInviteWhileLoggedIn>>,
  TError = Error,
>(
  id: SiteID,
  params: AcceptSiteUserInviteWhileLoggedInParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof acceptSiteUserInviteWhileLoggedIn>>, TError, TData>
    >;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getAcceptSiteUserInviteWhileLoggedInQueryOptions(id, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

export const getAcceptSiteUserInviteWhileLoggedInSuspenseQueryOptions = <
  TData = Awaited<ReturnType<typeof acceptSiteUserInviteWhileLoggedIn>>,
  TError = Error,
>(
  id: SiteID,
  params: AcceptSiteUserInviteWhileLoggedInParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<
        Awaited<ReturnType<typeof acceptSiteUserInviteWhileLoggedIn>>,
        TError,
        TData
      >
    >;
  }
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getAcceptSiteUserInviteWhileLoggedInQueryKey(id, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof acceptSiteUserInviteWhileLoggedIn>>> = ({
    signal,
  }) => acceptSiteUserInviteWhileLoggedIn(id, params, signal);

  const customOptions = addMetaToQuery(
    { ...queryOptions, queryKey, queryFn },
    { id, params },
    {
      url: `/v1/sites/${id}/users/accept`,
      operationId: 'app.controllers.site_users_controller.accept_site_user_invite_while_logged_in',
      operationName: 'acceptSiteUserInviteWhileLoggedIn',
    }
  );

  return customOptions as UseSuspenseQueryOptions<
    Awaited<ReturnType<typeof acceptSiteUserInviteWhileLoggedIn>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type AcceptSiteUserInviteWhileLoggedInSuspenseQueryResult = NonNullable<
  Awaited<ReturnType<typeof acceptSiteUserInviteWhileLoggedIn>>
>;
export type AcceptSiteUserInviteWhileLoggedInSuspenseQueryError = Error;

export function useAcceptSiteUserInviteWhileLoggedInSuspense<
  TData = Awaited<ReturnType<typeof acceptSiteUserInviteWhileLoggedIn>>,
  TError = Error,
>(
  id: SiteID,
  params: AcceptSiteUserInviteWhileLoggedInParams,
  options: {
    query: Partial<
      UseSuspenseQueryOptions<
        Awaited<ReturnType<typeof acceptSiteUserInviteWhileLoggedIn>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useAcceptSiteUserInviteWhileLoggedInSuspense<
  TData = Awaited<ReturnType<typeof acceptSiteUserInviteWhileLoggedIn>>,
  TError = Error,
>(
  id: SiteID,
  params: AcceptSiteUserInviteWhileLoggedInParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<
        Awaited<ReturnType<typeof acceptSiteUserInviteWhileLoggedIn>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useAcceptSiteUserInviteWhileLoggedInSuspense<
  TData = Awaited<ReturnType<typeof acceptSiteUserInviteWhileLoggedIn>>,
  TError = Error,
>(
  id: SiteID,
  params: AcceptSiteUserInviteWhileLoggedInParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<
        Awaited<ReturnType<typeof acceptSiteUserInviteWhileLoggedIn>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Accept site invite (token)
 */

export function useAcceptSiteUserInviteWhileLoggedInSuspense<
  TData = Awaited<ReturnType<typeof acceptSiteUserInviteWhileLoggedIn>>,
  TError = Error,
>(
  id: SiteID,
  params: AcceptSiteUserInviteWhileLoggedInParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<
        Awaited<ReturnType<typeof acceptSiteUserInviteWhileLoggedIn>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient
): UseSuspenseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getAcceptSiteUserInviteWhileLoggedInSuspenseQueryOptions(
    id,
    params,
    options
  );

  const query = useSuspenseQuery(queryOptions, queryClient) as UseSuspenseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Accepts a pending site invitation while signed in to your Rocket.net account.
 * @summary Accept site invite (logged in)
 */
export const acceptSiteUserInviteForSite = (id: SiteID, signal?: AbortSignal) => {
  return customFetch<SuccessResponse>({
    url: `/v1/sites/${id}/users/accept`,
    method: 'POST',
    signal,
  });
};

export const getAcceptSiteUserInviteForSiteMutationOptions = <
  TError = Error,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof acceptSiteUserInviteForSite>>,
    TError,
    { id: SiteID },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof acceptSiteUserInviteForSite>>,
  TError,
  { id: SiteID },
  TContext
> => {
  const mutationKey = ['acceptSiteUserInviteForSite'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof acceptSiteUserInviteForSite>>,
    { id: SiteID }
  > = props => {
    const { id } = props ?? {};

    return acceptSiteUserInviteForSite(id);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/sites/{id}/users/accept` },
    {
      operationId: 'app.controllers.site_users_controller.accept_site_user_invite_for_site',
      operationName: 'acceptSiteUserInviteForSite',
    }
  );

  return customOptions;
};

export type AcceptSiteUserInviteForSiteMutationResult = NonNullable<
  Awaited<ReturnType<typeof acceptSiteUserInviteForSite>>
>;

export type AcceptSiteUserInviteForSiteMutationError = Error;

/**
 * @summary Accept site invite (logged in)
 */
export const useAcceptSiteUserInviteForSite = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof acceptSiteUserInviteForSite>>,
      TError,
      { id: SiteID },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof acceptSiteUserInviteForSite>>,
  TError,
  { id: SiteID },
  TContext
> => {
  const mutationOptions = getAcceptSiteUserInviteForSiteMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Removes a collaborator's access to one site.
 * @summary Revoke site access
 */
export const deleteSiteUser = (id: SiteID, userId: number) => {
  return customFetch<SuccessResponse>({ url: `/v1/sites/${id}/users/${userId}`, method: 'DELETE' });
};

export const getDeleteSiteUserMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteSiteUser>>,
    TError,
    { id: SiteID; userId: number },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteSiteUser>>,
  TError,
  { id: SiteID; userId: number },
  TContext
> => {
  const mutationKey = ['deleteSiteUser'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteSiteUser>>,
    { id: SiteID; userId: number }
  > = props => {
    const { id, userId } = props ?? {};

    return deleteSiteUser(id, userId);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/sites/{id}/users/{userId}` },
    {
      operationId: 'app.controllers.site_users_controller.delete_site_user',
      operationName: 'deleteSiteUser',
    }
  );

  return customOptions;
};

export type DeleteSiteUserMutationResult = NonNullable<Awaited<ReturnType<typeof deleteSiteUser>>>;

export type DeleteSiteUserMutationError = Error;

/**
 * @summary Revoke site access
 */
export const useDeleteSiteUser = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deleteSiteUser>>,
      TError,
      { id: SiteID; userId: number },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof deleteSiteUser>>,
  TError,
  { id: SiteID; userId: number },
  TContext
> => {
  const mutationOptions = getDeleteSiteUserMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Sends a new invitation to an existing site user who has not yet accepted.
 * @summary Resend site invitation
 */
export const reinviteSiteUser = (id: SiteID, userId: number, signal?: AbortSignal) => {
  return customFetch<InviteSiteUserResponse>({
    url: `/v1/sites/${id}/users/${userId}/reinvite`,
    method: 'POST',
    signal,
  });
};

export const getReinviteSiteUserMutationOptions = <TError = Error, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof reinviteSiteUser>>,
    TError,
    { id: SiteID; userId: number },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof reinviteSiteUser>>,
  TError,
  { id: SiteID; userId: number },
  TContext
> => {
  const mutationKey = ['reinviteSiteUser'];
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof reinviteSiteUser>>,
    { id: SiteID; userId: number }
  > = props => {
    const { id, userId } = props ?? {};

    return reinviteSiteUser(id, userId);
  };

  const customOptions = addMetaToMutation(
    { ...mutationOptions, mutationFn },
    { url: `/v1/sites/{id}/users/{userId}/reinvite` },
    {
      operationId: 'app.controllers.site_users_controller.reinvite_site_user',
      operationName: 'reinviteSiteUser',
    }
  );

  return customOptions;
};

export type ReinviteSiteUserMutationResult = NonNullable<
  Awaited<ReturnType<typeof reinviteSiteUser>>
>;

export type ReinviteSiteUserMutationError = Error;

/**
 * @summary Resend site invitation
 */
export const useReinviteSiteUser = <TError = Error, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof reinviteSiteUser>>,
      TError,
      { id: SiteID; userId: number },
      TContext
    >;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof reinviteSiteUser>>,
  TError,
  { id: SiteID; userId: number },
  TContext
> => {
  const mutationOptions = getReinviteSiteUserMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
